import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Payments as CashIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import accountingService, { CashboxDto } from '../../../services/accountingService';
import { CourierCashHandoverState, courierModuleService, formatMoney } from './courierModuleService';

interface Props {
  open: boolean;
  courierId: number | null;
  courierName?: string | null;
  branchId?: string | null;
  onClose: () => void;
  /** Teslim alındı ya da reddedildi: panel yenilensin. */
  onChanged?: () => void;
}

const timeOf = (iso?: string | null) =>
  iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '—';

const parseAmount = (v: string): number | null => {
  const n = Number(v.replace(/\s/g, '').replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : Math.round(n * 100) / 100;
};

/**
 * Nakit teslim al: kuryenin bildirdiği tutarın tümü ya da kasiyerin gerçekten aldığı tutar teslim alınır.
 * Kurye istemeden de (uygulamayı kullanmayan kurye) aynı pencereden doğrudan teslim alınır.
 */
const CourierCashHandoverDialog: React.FC<Props> = ({ open, courierId, courierName, branchId, onClose, onChanged }) => {
  const { t } = useTranslation('salesSystem');
  const [state, setState] = useState<CourierCashHandoverState | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [mode, setMode] = useState<'all' | 'part'>('all');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [targetId, setTargetId] = useState<number | ''>('');
  const [cashboxes, setCashboxes] = useState<CashboxDto[]>([]);
  const [rejecting, setRejecting] = useState(false);

  const load = async (id: number) => {
    setLoading(true);
    try {
      const s = await courierModuleService.handoverState(id, branchId);
      setState(s);
      setTargetId((prev) => (prev === '' ? s.defaultCashboxId ?? '' : prev));
    } catch (e: any) {
      setError(e?.message || t('courierModule.handover.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !courierId) return;
    setState(null);
    setError(null);
    setDone(null);
    setMode('all');
    setAmount('');
    setNote('');
    setTargetId('');
    setRejecting(false);
    void load(courierId);
    accountingService.getCashboxes()
      .then((boxes) => setCashboxes(boxes.filter((c) => !c.IsDeleted && !(c.Name || '').startsWith('Kurye · '))))
      .catch(() => setCashboxes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courierId, branchId]);

  const pending = state?.pending ?? null;
  /** "Tümü": kuryenin bildirdiği tutar, istek yoksa elindeki nakit. */
  const allAmount = pending?.requestedAmount ?? state?.cashOnHand ?? 0;
  const received = mode === 'all' ? allAmount : parseAmount(amount);
  const remaining = state && received != null ? Math.round((state.cashOnHand - received) * 100) / 100 : null;
  const tooMuch = state != null && received != null && received > state.cashOnHand + 0.001;
  const needsTarget = !!state?.hasCourierCashbox && targetId === '';
  const canReceive = !!state && !busy && received != null && received > 0 && !tooMuch && !needsTarget;

  const targetName = useMemo(() => cashboxes.find((c) => c.Id === targetId)?.Name, [cashboxes, targetId]);

  const receive = async () => {
    if (!state || !courierId || !canReceive || received == null) return;
    setBusy(true);
    setError(null);
    try {
      const h = await courierModuleService.receiveHandover({
        courierId,
        handoverId: pending?.id ?? null,
        branchId: branchId || null,
        all: mode === 'all',
        amount: mode === 'all' ? null : received,
        targetCashboxId: targetId === '' ? null : Number(targetId),
        note: note.trim() || null,
      });
      setDone(t('courierModule.handover.received', { amount: formatMoney(h.receivedAmount), name: courierName || '' }));
      onChanged?.();
      await load(courierId);
      setMode('all');
      setAmount('');
      setNote('');
    } catch (e: any) {
      setError(e?.message || t('courierModule.handover.failed'));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!pending || !courierId) return;
    if (!rejecting) {
      setRejecting(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await courierModuleService.rejectHandover(pending.id, note.trim() || null);
      setDone(t('courierModule.handover.rejected'));
      setRejecting(false);
      onChanged?.();
      await load(courierId);
    } catch (e: any) {
      setError(e?.message || t('courierModule.handover.failed'));
    } finally {
      setBusy(false);
    }
  };

  const statusChip = (s: string) => (
    <Chip
      size="small"
      color={s === 'received' ? 'success' : s === 'rejected' || s === 'failed' ? 'error' : 'default'}
      variant={s === 'received' ? 'filled' : 'outlined'}
      label={t(`courierModule.handover.status.${s}`, { defaultValue: s })}
    />
  );

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CashIcon color="primary" />
        <span>{t('courierModule.handover.title', { name: courierName || '' })}</span>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !state && <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {done && <Alert severity="success" sx={{ mb: 1.5 }}>{done}</Alert>}
        {state && (
          <Stack spacing={1.5}>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25, display: 'grid', gap: 0.5 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t('courierModule.handover.onHand')}</Typography>
                <Typography fontWeight={800}>{formatMoney(state.cashOnHand)}</Typography>
              </Stack>
              {pending && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    {t('courierModule.handover.requested', { time: timeOf(pending.requestedAt) })}
                  </Typography>
                  <Typography fontWeight={800} color="warning.main">{formatMoney(pending.requestedAmount)}</Typography>
                </Stack>
              )}
              {pending?.courierNote && (
                <Typography variant="body2" color="text.secondary">“{pending.courierNote}”</Typography>
              )}
              {state.todayReceived > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">{t('courierModule.handover.todayReceived')}</Typography>
                  <Typography fontWeight={600}>{formatMoney(state.todayReceived)}</Typography>
                </Stack>
              )}
            </Box>

            {state.cashOnHand <= 0 && !pending ? (
              <Alert severity="info">{t('courierModule.handover.nothing')}</Alert>
            ) : (
              <>
                <Stack direction="row" spacing={1}>
                  <Chip
                    clickable
                    color={mode === 'all' ? 'primary' : 'default'}
                    variant={mode === 'all' ? 'filled' : 'outlined'}
                    label={t('courierModule.handover.all', { amount: formatMoney(allAmount) })}
                    onClick={() => setMode('all')}
                  />
                  <Chip
                    clickable
                    color={mode === 'part' ? 'primary' : 'default'}
                    variant={mode === 'part' ? 'filled' : 'outlined'}
                    label={t('courierModule.handover.part')}
                    onClick={() => setMode('part')}
                  />
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {mode === 'part' ? (
                    <TextField
                      size="small"
                      autoFocus
                      label={t('courierModule.handover.amount')}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      error={tooMuch}
                      helperText={tooMuch ? t('courierModule.handover.tooMuch', { amount: formatMoney(state.cashOnHand) }) : ' '}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
                    />
                  ) : (
                    <TextField size="small" label={t('courierModule.handover.amount')} value={formatMoney(allAmount)} disabled helperText=" " />
                  )}
                  <TextField
                    select
                    size="small"
                    label={t('courierModule.handover.cashbox')}
                    value={targetId}
                    onChange={(e) => setTargetId(e.target.value === '' ? '' : Number(e.target.value))}
                    error={needsTarget}
                    helperText={state.hasCourierCashbox ? ' ' : t('courierModule.handover.noCourierCashbox')}
                  >
                    <MenuItem value="">{t('courierModule.handover.pickCashbox')}</MenuItem>
                    {cashboxes.map((c) => <MenuItem key={c.Id} value={c.Id}>{c.Name}</MenuItem>)}
                  </TextField>
                </Box>
                <TextField size="small" label={t('courierModule.handover.note')} value={note} onChange={(e) => setNote(e.target.value)} />
                {received != null && received > 0 && !tooMuch && (
                  <Alert severity="info">
                    {state.hasCourierCashbox
                      ? t('courierModule.handover.planTransfer', {
                          amount: formatMoney(received),
                          cashbox: targetName || t('courierModule.handover.pickCashbox'),
                          remaining: formatMoney(remaining),
                        })
                      : t('courierModule.handover.planRecord', { amount: formatMoney(received), remaining: formatMoney(remaining) })}
                    {pending?.requestedAmount != null && Math.abs(received - pending.requestedAmount) > 0.001 && (
                      <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>
                        {t('courierModule.handover.differs', { requested: formatMoney(pending.requestedAmount), received: formatMoney(received) })}
                      </Box>
                    )}
                  </Alert>
                )}
                {rejecting && <Alert severity="warning">{t('courierModule.handover.rejectConfirm')}</Alert>}
              </>
            )}

            {state.today.length > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>{t('courierModule.handover.history')}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('courierModule.handover.cols.time')}</TableCell>
                      <TableCell align="right">{t('courierModule.handover.cols.requested')}</TableCell>
                      <TableCell align="right">{t('courierModule.handover.cols.received')}</TableCell>
                      <TableCell>{t('courierModule.handover.cols.by')}</TableCell>
                      <TableCell>{t('courierModule.handover.cols.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.today.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>{timeOf(h.resolvedAt || h.createdAt)}</TableCell>
                        <TableCell align="right">{h.requestedAmount != null ? formatMoney(h.requestedAmount) : '—'}</TableCell>
                        <TableCell align="right">{h.receivedAmount != null ? formatMoney(h.receivedAmount) : '—'}</TableCell>
                        <TableCell>
                          {h.resolvedBy || '—'}
                          {h.targetCashboxName && (
                            <Typography variant="caption" color="text.secondary" display="block">{h.targetCashboxName}</Typography>
                          )}
                        </TableCell>
                        <TableCell>{statusChip(h.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>{t('courierModule.actions.close')}</Button>
        {pending && (
          <Button color="error" disabled={busy} onClick={reject}>
            {rejecting ? t('courierModule.handover.rejectSure') : t('courierModule.handover.reject')}
          </Button>
        )}
        <Button variant="contained" color="success" disabled={!canReceive} onClick={receive}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {t('courierModule.handover.receive')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourierCashHandoverDialog;
